import { useState, useCallback } from 'react';
import { apiPost, ApiResponse, ApiError } from '../../services/api.service';

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    extraId: number;
    email: string;
    firstName: string;
    lastName: string;
    phone?: string;
    dateOfBirth?: string;
    nationality?: string;
    germanLevel?: string;
  };
}

interface UseLoginReturn {
  login: (email: string, password: string) => Promise<LoginResponse | null>;
  loading: boolean;
  error: string | null;
  success: boolean;
}

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_KEY = 'currentUser';

/**
 * Custom hook to handle user login
 * @returns Login function, loading state, error state, and success state
 */
export function useLogin(): UseLoginReturn {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResponse | null> => {
      try {
        setLoading(true);
        setError(null);
        setSuccess(false);

        const response: ApiResponse<LoginResponse> =
          await apiPost<LoginResponse>(
            '/auth/login',
            { email, password },
            {
              notification: {
                enabled: true,
                successMessage: 'Anmeldung erfolgreich!',
                errorMessage: 'Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Anmeldedaten.',
              },
            }
          );

        if (response.success && response.data) {
          // Store tokens and user data in localStorage
          localStorage.setItem(ACCESS_TOKEN_KEY, response.data.accessToken);
          localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken);
          localStorage.setItem(USER_KEY, JSON.stringify(response.data.user));

          setSuccess(true);
          return response.data;
        } else {
          setError(response.error || 'Anmeldung fehlgeschlagen');
          return null;
        }
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Ein unerwarteter Fehler ist bei der Anmeldung aufgetreten');
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    login,
    loading,
    error,
    success,
  };
}
